"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.selectDrillableItemsByAvailableDrillTargets = exports.selectImplicitDrillsByAvailableDrillTargets = exports.selectDrillableItemsByWidgetRef = exports.selectConfiguredAndImplicitDrillsByWidgetRef = exports.selectValidConfiguredDrillsByWidgetRef = exports.selectConfiguredDrillsByWidgetRef = exports.selectImplicitDrillsToUrlByWidgetRef = exports.selectImplicitDrillsDownByWidgetRef = void 0;
var tslib_1 = require("tslib");
// (C) 2020-2022 GoodData Corporation
var toolkit_1 = require("@reduxjs/toolkit");
var compact_1 = tslib_1.__importDefault(require("lodash/compact"));
var sdk_backend_spi_1 = require("@gooddata/sdk-backend-spi");
var sdk_model_1 = require("@gooddata/sdk-model");
var sdk_ui_1 = require("@gooddata/sdk-ui");
var selectors_1 = require("../_infra/selectors");
var layoutSelectors_1 = require("../layout/layoutSelectors");
var drillTargetsSelectors_1 = require("../drillTargets/drillTargetsSelectors");
var catalogSelectors_1 = require("../catalog/catalogSelectors");
var drillSelectors_1 = require("../drill/drillSelectors");
var configSelectors_1 = require("../config/configSelectors");
var flatMap_1 = tslib_1.__importDefault(require("lodash/flatMap"));
var accessibleDashboardsSelectors_1 = require("../accessibleDashboards/accessibleDashboardsSelectors");
var insightsSelectors_1 = require("../insights/insightsSelectors");
function drillDefinitionToPredicates(drill) {
    var origin;
    if (sdk_model_1.isDrillFromMeasure(drill.origin)) {
        origin = drill.origin.measure;
    }
    else if (sdk_model_1.isDrillFromAttribute(drill.origin)) {
        origin = drill.origin.attribute;
    }
    else {
        throw new sdk_backend_spi_1.UnexpectedError("Unknown drill origin!");
    }
    // add drillable items for all three types of objRefs that the origin measure can be
    return compact_1.default([
        sdk_model_1.isLocalIdRef(origin) && sdk_ui_1.HeaderPredicates.localIdentifierMatch(origin.localIdentifier),
        sdk_model_1.isIdentifierRef(origin) && sdk_ui_1.HeaderPredicates.identifierMatch(origin.identifier),
        sdk_model_1.isUriRef(origin) && sdk_ui_1.HeaderPredicates.uriMatch(origin.uri),
    ]);
}
function getDrillDownDefinitionsWithPredicates(availableDrillAttributes, attributesWithDrillDown) {
    var matchingAvailableDrillAttributes = availableDrillAttributes.filter(function (candidate) {
        return attributesWithDrillDown.some(function (attr) {
            return sdk_model_1.areObjRefsEqual(attr.attribute.ref, candidate.attribute.attributeHeader.formOf.ref);
        });
    });
    return matchingAvailableDrillAttributes.map(function (drill) {
        var matchingCatalogAttribute = attributesWithDrillDown.find(function (attr) {
            return sdk_model_1.areObjRefsEqual(attr.attribute.ref, drill.attribute.attributeHeader.formOf.ref);
        });
        return {
            drillDefinition: {
                type: "drillDown",
                origin: sdk_model_1.localIdRef(drill.attribute.attributeHeader.localIdentifier),
                target: matchingCatalogAttribute.attribute.drillDownStep,
            },
            predicates: [
                sdk_ui_1.HeaderPredicates.localIdentifierMatch(drill.attribute.attributeHeader.localIdentifier),
            ],
        };
    });
}
function getDrillToUrlDefinitionsWithPredicates(availableDrillAttributes, attributesWithDisplayFormLink) {
    var matchingAvailableDrillAttributes = availableDrillAttributes.filter(function (candidate) {
        return attributesWithDisplayFormLink.some(function (attr) {
            return sdk_model_1.areObjRefsEqual(attr.attribute.ref, candidate.attribute.attributeHeader.formOf.ref);
        });
    });
    return matchingAvailableDrillAttributes.map(function (targetAttribute) {
        var matchingCatalogAttribute = attributesWithDisplayFormLink.find(function (attr) {
            return sdk_model_1.areObjRefsEqual(attr.attribute.ref, targetAttribute.attribute.attributeHeader.formOf.ref);
        });
        var drillDefinition = {
            type: "drillToAttributeUrl",
            transition: "new-window",
            origin: {
                type: "drillFromAttribute",
                attribute: sdk_model_1.localIdRef(targetAttribute.attribute.attributeHeader.localIdentifier),
            },
            target: {
                displayForm: targetAttribute.attribute.attributeHeader.ref,
                hyperlinkDisplayForm: matchingCatalogAttribute.attribute.drillToAttributeLink,
            },
        };
        return {
            drillDefinition: drillDefinition,
            predicates: [
                // add drillable items for both types of objRefs that the header can be
                sdk_ui_1.HeaderPredicates.identifierMatch(targetAttribute.attribute.attributeHeader.identifier),
                sdk_ui_1.HeaderPredicates.uriMatch(targetAttribute.attribute.attributeHeader.uri),
            ],
        };
    });
}
function getDrillDefinitionsWithPredicates(insightWidgetDrills) {
    return insightWidgetDrills.map(function (drill) {
        return {
            drillDefinition: drill,
            predicates: drillDefinitionToPredicates(drill),
        };
    });
}
//
// Following selectors are for the 1st level insight widget (insight widget on the dashboard)
//
/**
 * @internal
 */
exports.selectImplicitDrillsDownByWidgetRef = selectors_1.createMemoizedSelector(function (ref) {
    return toolkit_1.createSelector(drillTargetsSelectors_1.selectDrillTargetsByWidgetRef(ref), catalogSelectors_1.selectAttributesWithDrillDown, configSelectors_1.selectEnableKPIDashboardImplicitDrillDown, function (availableDrillTargets, attributesWithDrillDown, isKPIDashboardImplicitDrillDown) {
        var _a, _b;
        if (isKPIDashboardImplicitDrillDown) {
            var availableDrillAttributes = (_b = (_a = availableDrillTargets === null || availableDrillTargets === void 0 ? void 0 : availableDrillTargets.availableDrillTargets) === null || _a === void 0 ? void 0 : _a.attributes) !== null && _b !== void 0 ? _b : [];
            return getDrillDownDefinitionsWithPredicates(availableDrillAttributes, attributesWithDrillDown);
        }
        return [];
    });
});
/**
 * @internal
 */
exports.selectImplicitDrillsToUrlByWidgetRef = selectors_1.createMemoizedSelector(function (ref) {
    return toolkit_1.createSelector(drillTargetsSelectors_1.selectDrillTargetsByWidgetRef(ref), catalogSelectors_1.selectAttributesWithDisplayFormLink, configSelectors_1.selectEnableClickableAttributeURL, function (availableDrillTargets, attributesWithLink, isClickableAttributeURLEnabled) {
        var _a, _b;
        if (isClickableAttributeURLEnabled) {
            var availableDrillAttributes = (_b = (_a = availableDrillTargets === null || availableDrillTargets === void 0 ? void 0 : availableDrillTargets.availableDrillTargets) === null || _a === void 0 ? void 0 : _a.attributes) !== null && _b !== void 0 ? _b : [];
            return getDrillToUrlDefinitionsWithPredicates(availableDrillAttributes, attributesWithLink);
        }
        return [];
    });
});
/**
 * @internal
 */
exports.selectConfiguredDrillsByWidgetRef = selectors_1.createMemoizedSelector(function (ref) {
    return toolkit_1.createSelector(layoutSelectors_1.selectWidgetDrills(ref), configSelectors_1.selectDisableDefaultDrills, configSelectors_1.selectEnableClickableAttributeURL, configSelectors_1.selectEnableKPIDashboardDrillToURL, configSelectors_1.selectEnableKPIDashboardDrillToInsight, configSelectors_1.selectEnableKPIDashboardDrillToDashboard, configSelectors_1.selectHideKpiDrillInEmbedded, configSelectors_1.selectIsEmbedded, function (drills, disableDefaultDrills, enableClickableAttributeURL, enableKPIDashboardDrillToURL, enableKPIDashboardDrillToInsight, enableKPIDashboardDrillToDashboard, hideKpiDrillInEmbedded, isEmbedded) {
        if (drills === void 0) { drills = []; }
        if (disableDefaultDrills) {
            return [];
        }
        var filteredDrills = tslib_1.__spread(drills).filter(function (drill) {
            var drillType = drill.type;
            switch (drillType) {
                case "drillToAttributeUrl": {
                    return enableClickableAttributeURL;
                }
                case "drillToCustomUrl": {
                    return enableKPIDashboardDrillToURL;
                }
                case "drillToDashboard": {
                    return enableKPIDashboardDrillToDashboard;
                }
                case "drillToInsight": {
                    return enableKPIDashboardDrillToInsight;
                }
                case "drillToLegacyDashboard": {
                    return !(isEmbedded && hideKpiDrillInEmbedded);
                }
                default: {
                    var unhandledType = drillType;
                    throw new sdk_backend_spi_1.UnexpectedError("Unhandled widget drill type: " + unhandledType);
                }
            }
        });
        return getDrillDefinitionsWithPredicates(filteredDrills);
    });
});
/**
 * @internal
 */
exports.selectValidConfiguredDrillsByWidgetRef = selectors_1.createMemoizedSelector(function (ref) {
    return toolkit_1.createSelector(exports.selectConfiguredDrillsByWidgetRef(ref), catalogSelectors_1.selectAllCatalogDisplayFormsMap, accessibleDashboardsSelectors_1.selectAccessibleDashboardsMap, insightsSelectors_1.selectInsightsMap, function (drills, displayFormsMap, accessibleDashboardsMap, insightsMap) {
        if (drills === void 0) { drills = []; }
        return drills.filter(function (drill) {
            switch (drill.drillDefinition.type) {
                case "drillToAttributeUrl": {
                    return displayFormsMap.get(drill.drillDefinition.target.hyperlinkDisplayForm);
                }
                case "drillToCustomUrl": {
                    return true;
                }
                case "drillToDashboard": {
                    // No drill target equals drill to the same dashboard
                    return (!drill.drillDefinition.target ||
                        accessibleDashboardsMap.get(drill.drillDefinition.target));
                }
                case "drillToInsight": {
                    return insightsMap.get(drill.drillDefinition.target);
                }
                case "drillToLegacyDashboard": {
                    return true;
                }
                case "drillDown": {
                    return true;
                }
                default: {
                    var unhandledType = drill.drillDefinition;
                    throw new sdk_backend_spi_1.UnexpectedError("Unhandled widget drill type: " + unhandledType);
                }
            }
        });
    });
});
var selectImplicitDrillToUrlPredicates = selectors_1.createMemoizedSelector(function (ref) {
    return toolkit_1.createSelector(exports.selectImplicitDrillsToUrlByWidgetRef(ref), function (drillToUrlDrills) {
        return flatMap_1.default(drillToUrlDrills, function (drill) { return drill.predicates; });
    });
});
var selectImplicitDrillDownPredicates = selectors_1.createMemoizedSelector(function (ref) {
    return toolkit_1.createSelector(exports.selectImplicitDrillsDownByWidgetRef(ref), function (drillDownDrills) {
        return flatMap_1.default(drillDownDrills, function (drill) { return drill.predicates; });
    });
});
var selectConfiguredDrillPredicates = selectors_1.createMemoizedSelector(function (ref) {
    return toolkit_1.createSelector(exports.selectValidConfiguredDrillsByWidgetRef(ref), function (configuredDrills) {
        if (configuredDrills === void 0) { configuredDrills = []; }
        return flatMap_1.default(configuredDrills, function (drill) { return drill.predicates; });
    });
});
/**
 * @internal
 */
exports.selectConfiguredAndImplicitDrillsByWidgetRef = selectors_1.createMemoizedSelector(function (ref) {
    return toolkit_1.createSelector(exports.selectValidConfiguredDrillsByWidgetRef(ref), exports.selectImplicitDrillsDownByWidgetRef(ref), exports.selectImplicitDrillsToUrlByWidgetRef(ref), function (configuredDrills, implicitDrillDownDrills, implicitDrillToUrlDrills) {
        return tslib_1.__spread(configuredDrills, implicitDrillDownDrills, implicitDrillToUrlDrills);
    });
});
/**
 * @internal
 */
exports.selectDrillableItemsByWidgetRef = selectors_1.createMemoizedSelector(function (ref) {
    return toolkit_1.createSelector(configSelectors_1.selectDisableDefaultDrills, drillSelectors_1.selectDrillableItems, selectConfiguredDrillPredicates(ref), selectImplicitDrillDownPredicates(ref), selectImplicitDrillToUrlPredicates(ref), function (disableDefaultDrills, drillableItems, configuredDrills, implicitDrillDownDrills, implicitDrillToUrlDrills) {
        var resolvedDrillableItems = tslib_1.__spread(drillableItems);
        if (!disableDefaultDrills) {
            resolvedDrillableItems.push.apply(resolvedDrillableItems, tslib_1.__spread(configuredDrills, implicitDrillDownDrills, implicitDrillToUrlDrills));
        }
        return resolvedDrillableItems;
    });
});
//
// Following selectors are for insight widget in drill dialog
//
/**
 * @internal
 */
exports.selectImplicitDrillsByAvailableDrillTargets = selectors_1.createMemoizedSelector(function (availableDrillTargets) {
    return toolkit_1.createSelector(catalogSelectors_1.selectAttributesWithDrillDown, catalogSelectors_1.selectAttributesWithDisplayFormLink, function (attributesWithDrillDown, attributesWithLink) {
        var _a;
        var availableDrillAttributes = (_a = availableDrillTargets === null || availableDrillTargets === void 0 ? void 0 : availableDrillTargets.attributes) !== null && _a !== void 0 ? _a : [];
        var drillDownDrills = getDrillDownDefinitionsWithPredicates(availableDrillAttributes, attributesWithDrillDown);
        var drillToUrlDrills = getDrillToUrlDefinitionsWithPredicates(availableDrillAttributes, attributesWithLink);
        return tslib_1.__spread(drillDownDrills, drillToUrlDrills);
    });
});
/**
 * @internal
 */
exports.selectDrillableItemsByAvailableDrillTargets = selectors_1.createMemoizedSelector(function (availableDrillTargets) {
    return toolkit_1.createSelector(exports.selectImplicitDrillsByAvailableDrillTargets(availableDrillTargets), function (implicitDrillDowns) {
        return flatMap_1.default(implicitDrillDowns, function (implicitDrill) { return implicitDrill.predicates; });
    });
});
//# sourceMappingURL=widgetDrillSelectors.js.map